import { useEffect, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { formatPlace } from "@/lib/districts";
import { GROUP_STATUS_LABEL, currentWeekStart } from "@/lib/group-coffee";

export const Route = createFileRoute("/_authenticated/admin/coffee-groups")({
  head: () => ({
    meta: [
      { title: "Кофе на районе — админка TurkeyDrive" },
      { name: "description", content: "Группы «Кофе на районе» этой недели." },
      { property: "og:title", content: "Кофе на районе — админка TurkeyDrive" },
      { property: "og:description", content: "Группы «Кофе на районе» этой недели." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: AdminCoffeeGroups,
});

function AdminCoffeeGroups() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [authorized, setAuthorized] = useState(false);
  useEffect(() => {
    (async () => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) return navigate({ to: "/profile" });
      const { data: prof } = await supabase.from("profiles").select("is_admin").eq("user_id", u.user.id).maybeSingle();
      if (!prof?.is_admin) return navigate({ to: "/profile" });
      setAuthorized(true);
    })();
  }, [navigate]);

  const { data: groups = [], isLoading } = useQuery({
    queryKey: ["admin", "coffee-groups"],
    enabled: authorized,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("coffee_groups")
        .select("id, city, district, status, created_at, coffee_group_members(user_id, status, outcome)")
        .eq("week_start", currentWeekStart())
        .order("city");
      if (error) throw error;
      const ids = [...new Set((data ?? []).flatMap((g) => g.coffee_group_members.map((m) => m.user_id)))];
      const names = new Map<string, string>();
      if (ids.length) {
        const { data: ps } = await supabase.from("profiles").select("user_id, full_name").in("user_id", ids);
        for (const p of ps ?? []) names.set(p.user_id, p.full_name);
      }
      return (data ?? []).map((g) => ({ ...g, members: g.coffee_group_members.map((m) => ({ ...m, name: names.get(m.user_id) ?? "—" })) }));
    },
  });

  const build = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.rpc("admin_build_coffee_groups");
      if (error) throw error;
      return data as number;
    },
    onSuccess: (n) => { toast.success(n ? `Собрано групп: ${n}` : "Новых групп не получилось — мало участников"); qc.invalidateQueries({ queryKey: ["admin", "coffee-groups"] }); },
    onError: (e: Error) => toast.error(e.message),
  });

  if (!authorized) return <div className="mx-auto max-w-3xl px-4 py-8 text-sm text-td-muted">Проверка доступа…</div>;

  return (
    <main className="mx-auto min-w-0 max-w-3xl px-4 py-6">
      <Link to="/profile" className="text-sm text-td-muted hover:text-td-dark">← Профиль</Link>
      <h1 className="mt-2 font-display text-2xl font-bold text-td-dark">Кофе на районе</h1>
      <p className="mt-1 text-sm text-td-muted">Группы этой недели. Автосбор — по средам в 11:00 по Турции.</p>
      <Button className="mt-4 h-11 w-full bg-td-teal hover:bg-td-teal/90 sm:w-auto" disabled={build.isPending} onClick={() => build.mutate()}>
        {build.isPending ? "Собираем…" : "Собрать группы сейчас"}
      </Button>
      {isLoading ? <p className="mt-6 text-sm text-td-muted">Загружаем…</p> : groups.length === 0 ? (
        <p className="mt-6 text-sm text-td-muted">На этой неделе групп пока нет.</p>
      ) : (
        <ul className="mt-6 space-y-3">
          {groups.map((g) => {
            const joined = g.members.filter((m) => m.status === "joined").length;
            const met = g.members.filter((m) => m.outcome === "met").length;
            return (
              <li key={g.id} className="rounded-2xl border border-td-line bg-white p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Link to="/coffee/group/$groupId" params={{ groupId: g.id }} className="min-w-0 font-semibold text-td-dark underline-offset-2 hover:underline">{formatPlace(g.city, g.district)}</Link>
                  <span className="rounded-full bg-td-soft px-2.5 py-0.5 text-xs text-td-dark">{GROUP_STATUS_LABEL[g.status] ?? g.status}</span>
                </div>
                <p className="mt-1 text-xs text-td-muted">Подтвердили {joined} из {g.members.length}{met ? ` · встретились: ${met}` : ""}</p>
                <p className="mt-2 break-words text-sm text-td-dark/85">{g.members.map((m) => `${m.name}${m.status === "joined" ? " ✓" : m.status === "declined" ? " ✕" : ""}`).join(", ")}</p>
              </li>
            );
          })}
        </ul>
      )}
    </main>
  );
}
